import { useState, useRef, useSyncExternalStore } from 'react'
import { useAutoRetry } from '../../hooks/useAutoRetry'
import ErrorRetryBar from '../../components/ErrorRetryBar'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Input, Text, Spinner, Checkbox, Badge,
  Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
  MessageBar, MessageBarBody,
  Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, MenuDivider,
  makeStyles, tokens,
} from '@fluentui/react-components'
import {
  Add24Regular, SearchRegular,
  ArrowDownload24Regular, ArrowUpload24Regular,
  EditRegular,
  CheckmarkCircle24Regular, DismissCircle24Regular, Warning24Regular,
  ChevronDownRegular,
} from '@fluentui/react-icons'
import {
  pensionContributionsService,
  PensionContributionListItem,
  FrequencyLabels,
  ContributionTypeLabels,
} from '../../services/pensionContributionsService'
import { exportsService } from '../../services/exportsService'
import { importsService, ImportResult } from '../../services/importsService'
import { useAuth } from '../../auth/useAuth'

function useIsMobile(breakpoint = 640) {
  return useSyncExternalStore(
    cb => { window.addEventListener('resize', cb); return () => window.removeEventListener('resize', cb) },
    () => window.innerWidth <= breakpoint,
    () => false,
  )
}

const useStyles = makeStyles({
  root:     { display: 'flex', flexDirection: 'column', gap: '16px', height: '100%', minHeight: 0 },
  header:   { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  spacer:   { flex: 1 },
  toolbar:  { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '4px' },
  row:      { cursor: 'pointer', '&:hover': { backgroundColor: tokens.colorNeutralBackground1Hover } },
  th: {
    fontSize: '11px', fontWeight: '600',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em', color: tokens.colorNeutralForeground3,
  },
  twoLine:  { display: 'flex', flexDirection: 'column' as const, gap: '1px', minWidth: 0 },
  sub:      { fontSize: '11px', color: tokens.colorNeutralForeground3 },
  importRow: {
    display: 'flex', alignItems: 'flex-start', gap: '8px',
    padding: '6px 0', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  importMessages: { display: 'flex', flexDirection: 'column' as const, gap: '2px' },
  importIcon:     { marginTop: '2px', flexShrink: 0 },
})

function fmt(iso: string | null) {
  if (!iso) return '—'
  return iso.slice(0, 10).split('-').reverse().join('.')
}

function isActive(item: PensionContributionListItem) {
  if (!item.validTo) return true
  return item.validTo >= new Date().toISOString().slice(0, 10)
}

export default function PensionContributionsPage() {
  const styles = useStyles()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { isHR, isAdmin } = useAuth()
  const canEdit = isHR || isAdmin

  const [search, setSearch]             = useState('')
  const [showInactive, setShowInactive] = useState(false)

  const [isExporting, setIsExporting] = useState(false)
  const [isExportingPayroll, setIsExportingPayroll] = useState(false)
  const [exportError, setExportError]   = useState<string | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [importError, setImportError]   = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['pension-contributions', search, showInactive],
    queryFn: () => pensionContributionsService.getAll({
      search: search || undefined,
      activeOnly: !showInactive,
    }),
  })

  const { autoRetryCount, autoRetryCountdown, maxRetries, resetAndRetry } = useAutoRetry(isError, refetch)

  const handleExport = async () => {
    setIsExporting(true)
    setExportError(null)
    try {
      await exportsService.downloadPensionContributions()
    } catch (err: unknown) {
      const anyErr = err as any
      const status = anyErr?.response?.status
      const detail = anyErr?.response?.data?.detail
      setExportError(detail ?? (status ? `Chyba ${status} při exportu` : 'Chyba při exportu'))
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportPayroll = async () => {
    setIsExportingPayroll(true)
    setExportError(null)
    try {
      await exportsService.downloadPensionContributionsPayroll()
    } catch (err: unknown) {
      const anyErr = err as any
      const status = anyErr?.response?.status
      const detail = anyErr?.response?.data?.detail
      setExportError(detail ?? (status ? `Chyba ${status} při exportu` : 'Chyba při exportu'))
    } finally {
      setIsExportingPayroll(false)
    }
  }

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsImporting(true)
    setImportError(null)
    try {
      const result = await importsService.importPensionContributions(file)
      setImportResult(result)
      qc.invalidateQueries({ queryKey: ['pension-contributions'] })
    } catch (err: unknown) {
      const anyErr = err as any
      setImportError(anyErr?.response?.data?.detail ?? anyErr?.message ?? 'Chyba při importu')
    } finally {
      setIsImporting(false)
      e.target.value = ''
    }
  }

  return (
    <div className={styles.root}>
      {/* Nadpis */}
      <div className={styles.header}>
        <Text size={600} weight="semibold">Příspěvky na stáří</Text>
        <div className={styles.spacer} />
        {canEdit && (
          <>
            <input ref={fileInputRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleImport} />
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button icon={<ChevronDownRegular />} iconPosition="after">
                  Export &amp; Import
                </Button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem
                    icon={isExportingPayroll ? <Spinner size="tiny" /> : <ArrowDownload24Regular />}
                    onClick={handleExportPayroll}
                    disabled={isExportingPayroll}
                  >
                    Export příspěvků pro mzdovou účetní
                  </MenuItem>
                  <MenuDivider />
                  <MenuItem
                    icon={isExporting ? <Spinner size="tiny" /> : <ArrowDownload24Regular />}
                    onClick={handleExport}
                    disabled={isExporting}
                  >
                    Export příspěvků (Excel)
                  </MenuItem>
                  <MenuItem
                    icon={isImporting ? <Spinner size="tiny" /> : <ArrowUpload24Regular />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isImporting}
                  >
                    Import příspěvků
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
            <Button
              appearance="primary" icon={<Add24Regular />}
              onClick={() => navigate('/pension-contributions/new')}
            >
              Přidat záznam
            </Button>
          </>
        )}
      </div>

      {exportError && (
        <MessageBar intent="error">
          <MessageBarBody>Export: {exportError}</MessageBarBody>
        </MessageBar>
      )}
      {importError && (
        <MessageBar intent="error">
          <MessageBarBody>{importError}</MessageBarBody>
        </MessageBar>
      )}

      {/* Toolbar — filtry */}
      <div className={styles.toolbar}>
        <Input
          style={{ minWidth: 280 }}
          placeholder="Hledat zaměstnance nebo penzijní ústav..."
          contentBefore={<SearchRegular />}
          value={search}
          onChange={(_, d) => setSearch(d.value)}
        />
        <Checkbox
          label="Zobrazit ukončené"
          checked={showInactive}
          onChange={(_, d) => setShowInactive(!!d.checked)}
        />
        <div className={styles.spacer} />
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          Celkem: {data.length}
        </Text>
      </div>

      <ErrorRetryBar
        isError={isError}
        message="Nepodařilo se načíst příspěvky na stáří."
        autoRetryCount={autoRetryCount}
        autoRetryCountdown={autoRetryCountdown}
        maxRetries={maxRetries}
        onRetry={resetAndRetry}
      />

      {/* Tabulka */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      {isLoading ? (
        <Spinner label="Načítám…" />
      ) : (
        <Table style={{ tableLayout: 'fixed' as const }}>
          <TableHeader>
            <TableRow className="sticky-head">
              <TableHeaderCell className={styles.th} style={{ width: '20%' }}>Zaměstnanec</TableHeaderCell>
              {!isMobile && <TableHeaderCell className={styles.th} style={{ width: '18%' }}>Penzijní ústav</TableHeaderCell>}
              {!isMobile && <TableHeaderCell className={styles.th} style={{ width: '10%' }}>Výše</TableHeaderCell>}
              {!isMobile && <TableHeaderCell className={styles.th} style={{ width: '11%' }}>Frekvence</TableHeaderCell>}
              {!isMobile && <TableHeaderCell className={styles.th} style={{ width: '11%' }}>Typ</TableHeaderCell>}
              <TableHeaderCell className={styles.th} style={{ width: '11%' }}>Zahájení</TableHeaderCell>
              {!isMobile && <TableHeaderCell className={styles.th} style={{ width: '11%' }}>Ukončení</TableHeaderCell>}
              <TableHeaderCell className={styles.th} style={{ width: '8%' }}>Stav</TableHeaderCell>
              {canEdit && !isMobile && <TableHeaderCell style={{ width: '100px' }} />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 9 : 8}>
                  <Text style={{ color: tokens.colorNeutralForeground3 }}>Žádné záznamy</Text>
                </TableCell>
              </TableRow>
            ) : (
              data.map(item => (
                <TableRow key={item.id} className={styles.row}
                  onClick={() => navigate(`/pension-contributions/${item.id}`)}>
                  <TableCell>
                    <div className={styles.twoLine}>
                      <Text size={300} weight="semibold">{item.employeeFullName}</Text>
                      {item.departmentName && <span className={styles.sub}>{item.departmentName}</span>}
                    </div>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Text size={300}>{item.pensionProvider}</Text>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Text size={300} weight="semibold">{item.amount.toLocaleString('cs-CZ')} Kč</Text>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Text size={300}>{FrequencyLabels[item.frequency]}</Text>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Text size={300}>{ContributionTypeLabels[item.contributionType]}</Text>
                    </TableCell>
                  )}
                  <TableCell>
                    <Text size={300}>{fmt(item.validFrom)}</Text>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Text size={300}>{fmt(item.validTo)}</Text>
                    </TableCell>
                  )}
                  <TableCell>
                    {isActive(item)
                      ? <Badge color="success" appearance="tint">Aktivní</Badge>
                      : <Badge color="subtle" appearance="tint">Ukončen</Badge>
                    }
                  </TableCell>
                  {canEdit && !isMobile && (
                    <TableCell onClick={e => e.stopPropagation()} style={{ textAlign: 'right' }}>
                      <Button
                        size="small" appearance="subtle" icon={<EditRegular />}
                        onClick={() => navigate(`/pension-contributions/${item.id}/edit`)}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
      </div>

      {/* Dialog — výsledky importu */}
      <Dialog open={importResult !== null} onOpenChange={() => setImportResult(null)}>
        <DialogSurface style={{ maxWidth: 600 }}>
          <DialogTitle>Výsledek importu</DialogTitle>
          <DialogBody>
            <DialogContent>
              {importResult && (
                <>
                  <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
                    <Text><CheckmarkCircle24Regular style={{ color: tokens.colorPaletteGreenForeground1, verticalAlign: 'middle' }} /> Vytvořeno: <strong>{importResult.created}</strong></Text>
                    <Text><CheckmarkCircle24Regular style={{ color: tokens.colorBrandForeground1, verticalAlign: 'middle' }} /> Aktualizováno: <strong>{importResult.updated}</strong></Text>
                    <Text><DismissCircle24Regular style={{ color: tokens.colorPaletteRedForeground1, verticalAlign: 'middle' }} /> Přeskočeno: <strong>{importResult.skipped}</strong></Text>
                  </div>
                  {importResult.rows.filter(r => r.messages.length > 0 || r.action === 'skipped').length > 0 && (
                    <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                      {importResult.rows
                        .filter(r => r.messages.some(m => m.type === 'error') || r.action === 'skipped')
                        .map(row => (
                          <div key={row.rowNumber} className={styles.importRow}>
                            <div className={styles.importIcon}>
                              {row.action === 'skipped'
                                ? <DismissCircle24Regular style={{ color: tokens.colorPaletteRedForeground1 }} />
                                : <Warning24Regular style={{ color: tokens.colorPaletteYellowForeground1 }} />}
                            </div>
                            <div>
                              <Text weight="semibold" size={200}>Řádek {row.rowNumber} — {row.name}</Text>
                              <div className={styles.importMessages}>
                                {row.messages.map((msg, i) => (
                                  <Text key={i} size={100} style={{ color: msg.type === 'error' ? tokens.colorPaletteRedForeground1 : tokens.colorPaletteYellowForeground1 }}>
                                    {msg.type === 'error' ? '❌' : '⚠️'} {msg.text}
                                  </Text>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setImportResult(null)}>Zavřít</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
